// src/components/flux/FluxVisualizer.jsx
import React, { useState, useEffect, useRef } from 'react';
import FluxNode from './FluxNode';
import FluxConnection from './FluxConnection';
import FluxToolbar from './FluxToolbar';
import './FluxVisualizer.css';

const FluxVisualizer = ({ botId }) => {
  // Estado para los nodos y conexiones
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);

  // Datos de ejemplo para un flujo de bot
  useEffect(() => {
    // En una implementación real, aquí cargaríamos los datos del backend
    const demoNodes = [
      {
        id: 1,
        type: 'trigger',
        name: 'Entrada de WhatsApp',
        position: { x: 100, y: 150 },
        data: { 
          provider: 'WhatsApp',
          service: 'input',
          config: { webhook: true }
        }
      },
      {
        id: 2,
        type: 'process',
        name: 'Procesamiento de Mensaje',
        position: { x: 400, y: 150 },
        data: {
          actions: ['extraer_entidades', 'clasificar_intención']
        }
      },
      {
        id: 3,
        type: 'decision',
        name: 'Decisión de Ruta',
        position: { x: 700, y: 150 },
        data: {
          conditions: [
            { field: 'intención', operator: '==', value: 'consulta_producto' },
            { field: 'intención', operator: '==', value: 'soporte' }
          ]
        }
      },
      {
        id: 4,
        type: 'action',
        name: 'Consulta CRM',
        position: { x: 700, y: 350 },
        data: {
          provider: 'SalesforceCRM',
          service: 'crm',
          method: 'buscar_producto'
        }
      },
      {
        id: 5,
        type: 'output',
        name: 'Respuesta al Usuario',
        position: { x: 1000, y: 150 },
        data: {
          provider: 'WhatsApp',
          service: 'output',
          template: 'respuesta_producto'
        }
      }
    ];

    const demoConnections = [
      { id: 1, source: 1, target: 2, label: 'Mensaje recibido' },
      { id: 2, source: 2, target: 3, label: 'Intención identificada' },
      { id: 3, source: 3, target: 4, label: 'Consulta producto' },
      { id: 4, source: 4, target: 5, label: 'Información recuperada' },
      { id: 5, source: 3, target: 5, label: 'Respuesta directa' }
    ];

    setNodes(demoNodes);
    setConnections(demoConnections);
  }, [botId]);

  // Función para manejar el inicio del arrastre de un nodo
  const handleNodeDragStart = (nodeId, e) => {
    if (selectedNode !== nodeId) {
      setSelectedNode(nodeId);
    }
    
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setIsDragging(true);
      
      // Calcular el desplazamiento del ratón respecto al nodo
      const nodeElement = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - nodeElement.left;
      const offsetY = e.clientY - nodeElement.top;
      
      setDragOffset({ x: offsetX, y: offsetY });
    }
  };

  // Función para manejar el arrastre de un nodo
  const handleNodeDrag = (e) => {
    if (isDragging && selectedNode) {
      e.preventDefault();
      
      const canvas = canvasRef.current.getBoundingClientRect();
      const newX = (e.clientX - canvas.left - dragOffset.x) / zoom;
      const newY = (e.clientY - canvas.top - dragOffset.y) / zoom;
      
      setNodes(prevNodes => 
        prevNodes.map(node => 
          node.id === selectedNode 
            ? { ...node, position: { x: newX, y: newY } } 
            : node
        )
      );
    }
  };

  // Función para manejar el fin del arrastre
  const handleNodeDragEnd = () => {
    setIsDragging(false);
  };

  // Función para aumentar el zoom
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  };

  // Función para reducir el zoom
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  // Función para resetear el zoom
  const handleZoomReset = () => {
    setZoom(1);
  };

  // Función para añadir un nuevo nodo
  const handleAddNode = (type) => {
    const newId = nodes.length > 0 ? Math.max(...nodes.map(n => n.id)) + 1 : 1;
    
    // Posición predeterminada para el nuevo nodo
    const canvas = canvasRef.current.getBoundingClientRect();
    const newX = (canvas.width / 2) / zoom;
    const newY = (canvas.height / 2) / zoom;

    const newNode = {
      id: newId,
      type,
      name: `Nuevo ${getNodeTypeName(type)}`,
      position: { x: newX, y: newY },
      data: {}
    };

    setNodes([...nodes, newNode]);
    setSelectedNode(newId);
  };

  // Función para eliminar un nodo
  const handleDeleteNode = (nodeId) => {
    // Eliminar el nodo
    setNodes(nodes.filter(node => node.id !== nodeId));
    
    // Eliminar las conexiones asociadas al nodo
    setConnections(connections.filter(
      conn => conn.source !== nodeId && conn.target !== nodeId
    ));
    
    if (selectedNode === nodeId) {
      setSelectedNode(null);
    }
  };

  // Función auxiliar para traducir el tipo de nodo
  const getNodeTypeName = (type) => {
    const typeMap = {
      trigger: 'Disparador',
      process: 'Proceso',
      decision: 'Decisión',
      action: 'Acción',
      output: 'Salida'
    };
    return typeMap[type] || type;
  };

  return (
    <div className="flux-visualizer-container">
      <FluxToolbar 
        onAddNode={handleAddNode}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        zoom={zoom}
      />
      
      <div 
        className="flux-canvas"
        ref={canvasRef}
        onMouseMove={handleNodeDrag}
        onMouseUp={handleNodeDragEnd}
        onMouseLeave={handleNodeDragEnd}
        style={{ transform: `scale(${zoom})` }}
      >
        {/* Renderizar conexiones */}
        {connections.map(connection => {
          const sourceNode = nodes.find(node => node.id === connection.source);
          const targetNode = nodes.find(node => node.id === connection.target);
          
          if (sourceNode && targetNode) {
            return (
              <FluxConnection
                key={`connection-${connection.id}`}
                id={connection.id}
                source={sourceNode}
                target={targetNode}
                label={connection.label}
              />
            );
          }
          return null;
        })}
        
        {/* Renderizar nodos */}
        {nodes.map(node => (
          <FluxNode 
            key={`node-${node.id}`}
            id={node.id}
            type={node.type}
            name={node.name}
            position={node.position}
            data={node.data}
            isSelected={selectedNode === node.id}
            onDragStart={(e) => handleNodeDragStart(node.id, e)}
            onDelete={() => handleDeleteNode(node.id)}
          />
        ))}
      </div>
      
      {/* Panel de propiedades del nodo seleccionado */}
      {selectedNode && (
        <div className="node-properties-panel">
          <h3>Propiedades del Nodo</h3>
          {/* Aquí iría un formulario para editar las propiedades del nodo seleccionado */}
          <div className="property-form">
            <div className="form-group">
              <label>Nombre</label>
              <input 
                type="text" 
                value={nodes.find(n => n.id === selectedNode)?.name || ''}
                onChange={(e) => {
                  const newName = e.target.value;
                  setNodes(prev => prev.map(node => 
                    node.id === selectedNode 
                      ? { ...node, name: newName } 
                      : node
                  ));
                }}
              />
            </div>
            {/* Aquí se podrían mostrar más propiedades específicas según el tipo de nodo */}
          </div>
        </div>
      )}
    </div>
  );
};

export default FluxVisualizer;